import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { AllocationChart, Bars, CashFlowChart, CLASS_LABEL, NetWorthChart } from './charts'
import { GROUP_LABEL, type AccountSummary, type Group } from './lib/analysis'
import type { Finding, ReviewProgress, Saved } from './lib/advice'
import type { Book } from './lib/book'
import type { Row } from './lib/google/sheets'
import type { Filter } from './Transactions'
import { MerchantSearch } from './MerchantSearch'
import { WhatChanged } from './WhatChanged'
import { CategoryTrends, EmergencyFund, GainsChart, IncomeFlow, NetWorthBands, RecurringChart, SavingsRateChart, SpendingCalendar, VersusUsualChart } from './trendCharts'
import {
  annualFees,
  categoryTrends,
  comparisonMonth,
  dailySpending,
  emergencyTarget,
  firstDay,
  fullMonths,
  monthFlows,
  offeredRanges,
  RANGES,
  recurringByMonth,
  savingsRates,
  spanOf,
  spendingSummary,
  versusUsual,
  withinSpan,
  type RangeId,
} from './lib/trends'
import { summarise } from './lib/usage'
import { monthsOfData } from './lib/dates'

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
const whole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const pct = (n: number) => `${Math.round(n * 100)}%`

const AREA: Record<string, string> = {
  investing: 'Investing',
  retirement: 'Retirement',
  tax: 'Tax',
  cash: 'Cash',
  debt: 'Debt',
  spending: 'Spending',
  cards: 'Credit cards',
  insurance: 'Insurance',
  estate: 'Estate',
  data: 'Missing data',
}

/** Icon + word for each tone; the concern style is bolder, never just a different hue. */
const TONE: Record<Finding['tone'], { icon: string; word: string }> = {
  strength: { icon: '▲', word: 'Strength' },
  concern: { icon: '!', word: 'Concern' },
  note: { icon: '•', word: 'Note' },
}

const PRIORITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 }

const GROUP_ORDER: Group[] = ['cash', 'taxable', 'retirement', 'hsa', 'education', 'property', 'other', 'debt']

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'spending', label: 'Spending' },
  { id: 'investments', label: 'Investments' },
  { id: 'accounts', label: 'Accounts' },
] as const
type TabId = (typeof TABS)[number]['id']

const label = (s: string) => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

const RANGE_KEY = 'finance-buddy.range'

/** The range picked last time on this browser; a year, the review's window, otherwise. */
function savedRange(): RangeId {
  try {
    const id = localStorage.getItem(RANGE_KEY)
    return RANGES.find((r) => r.id === id)?.id ?? '1y'
  } catch {
    return '1y'
  }
}

function tabFromHash(): TabId {
  const h = typeof location === 'undefined' ? '' : location.hash.slice(1)
  return TABS.find((t) => t.id === h)?.id ?? 'overview'
}

interface Props {
  book: Book
  saved?: Saved
  /** Opens the transactions drawer. */
  onDrill: (filter: Filter) => void
  /** Puts a question in the question box. */
  onAsk: (text: string) => void
  /** Corrections changed the figures since the review was written. */
  reviewStale?: boolean
  /** Changes each time something asks a question, so the Overview tab (which holds the question box) is shown. */
  askNonce?: number
  analysing: boolean
  /** Where a running review has got to. */
  progress?: ReviewProgress
  error?: string
  /** Shown with the error when a finished review is waiting to be saved. */
  onRetrySave?: () => void
  onRefresh: () => void
  onStatus: (recId: string, status: 'new' | 'done' | 'dismissed') => void
  /** Shown straight after the review: the question box. */
  children?: ReactNode
  /** The spreadsheet, for the pointer to its Manual tab. */
  sheetUrl?: string
}

export function Dashboard({ book, saved, analysing, progress, error, onRetrySave, onRefresh, onStatus, children, sheetUrl, onDrill, onAsk, reviewStale, askNonce }: Props) {
  const snapshot = book.snapshot
  const s = snapshot.spending
  const asOf = snapshot.as_of
  // The time range the Overview and Spending charts show. The stat tiles and the
  // written review stay on the twelve-month window.
  const [picked, setPicked] = useState<RangeId>(savedRange)
  const pick = (id: RangeId) => {
    setPicked(id)
    try {
      localStorage.setItem(RANGE_KEY, id)
    } catch {
      // Private window: the range still changes, it just isn't remembered.
    }
  }
  const earliest =
    [...book.ledger.filter((r) => r.cash && (!asOf || r.date <= asOf)).map((r) => r.date), ...snapshot.net_worth.slice(0, 1).map((p) => `${p.month}-01`)].sort()[0] ?? null
  const offered = asOf ? offeredRanges(earliest, asOf) : new Set<RangeId>()
  // A range the statements don't reach falls back to the longest one they do.
  const at = RANGES.findIndex((r) => r.id === picked)
  const range = RANGES.slice(0, at + 1).reverse().find((r) => offered.has(r.id)) ?? RANGES[2]
  const span = asOf ? spanOf(asOf, range.months) : null
  const ledger = useMemo(() => (span ? withinSpan(book.ledger, span) : book.ledger), [book.ledger, span?.from, span?.to])
  const spanText = span ? `${firstDay(span, earliest) ?? '—'} to ${span.to}` : ''
  const topOf = (month: string) =>
    ledger
      .filter((r) => r.in_window && r.month === month && r.treatment === 'spending')
      .sort((a, b) => a.amount - b.amount)
      .slice(0, 3)
      .map((r) => ({ merchant: r.merchant, amount: -r.amount }))
  // A list opened from a chart covers the range the chart shows.
  const drill = (f: Filter) => onDrill(f.windowOnly && span ? { ...f, span, title: f.month ? f.title : `${f.title}, ${spanText}` } : f)
  const spendingIn = (title: string, f: Partial<Filter>) => drill({ title, treatments: ['spending'], windowOnly: true, ...f })
  const [tab, setTab] = useState<TabId>(tabFromHash)
  const choose = (t: TabId) => {
    setTab(t)
    try {
      history.replaceState(null, '', `#${t}`)
    } catch {
      // Sandboxed frame: the tab still changes, it just isn't in the URL.
    }
  }
  // Back, forward and a typed #spending change the tab too.
  useEffect(() => {
    const follow = () => setTab(tabFromHash())
    window.addEventListener('hashchange', follow)
    return () => window.removeEventListener('hashchange', follow)
  }, [])
  // "Ask about this" from any tab brings the question box into view.
  useEffect(() => {
    if (askNonce) setTab('overview')
  }, [askNonce])
  const cf = snapshot.cash_flow
  const inv = snapshot.investments
  const mortgage = snapshot.accounts.find((a) => a.kind === 'mortgage')
  const flows = monthFlows(ledger)
  const months = fullMonths(flows.map((m) => m.month), asOf, range.months)
  const shownFlows = flows.filter((m) => months.includes(m.month))
  const rates = savingsRates(shownFlows)
  const compared = asOf ? comparisonMonth(asOf) : null
  const usual = compared ? versusUsual(ledger, months, compared) : []
  const sp = spendingSummary(ledger)
  const income = flows.reduce((sum, m) => sum + m.income, 0)
  // Divided by the months of data in the range, not the calendar months it touches.
  const perMonth = sp.from && asOf ? sp.total / Math.min(range.months ?? Infinity, monthsOfData(sp.from, span?.to ?? asOf)) : 0
  const netWorth = span?.from ? snapshot.net_worth.filter((p) => p.month >= span.from!.slice(0, 7)) : snapshot.net_worth
  const fees = annualFees(inv.top_holdings)
  const feeTotal = fees.reduce((sum, f) => sum + f.total, 0)
  // Sits above the charts it controls; the review, the merchant search and the tiles above ignore it.
  const rangePicker = span && (
    <div className="range" role="group" aria-label="Time range of the charts below">
      <span className="label">Charts below show</span>
      {RANGES.map((r) => (
        <button
          key={r.id}
          className={r.id === range.id ? 'on' : undefined}
          aria-pressed={r.id === range.id}
          disabled={!offered.has(r.id)}
          title={offered.has(r.id) ? undefined : 'The statements don’t go back that far'}
          onClick={() => pick(r.id)}
        >
          {r.label}
        </button>
      ))}
      <span className="label num">{spanText}</span>
    </div>
  )

  return (
    <>
      <dl className="stats">
        <Stat label="Net worth" value={whole.format(snapshot.totals.net)} sub={`${whole.format(snapshot.totals.assets)} assets − ${whole.format(snapshot.totals.debts)} owed`} />
        <Stat label="Invested" value={whole.format(snapshot.totals.invested)} sub={`${pct(snapshot.totals.invested / (snapshot.totals.assets || 1))} of assets`} />
        <Stat
          label="Cash"
          value={whole.format(snapshot.totals.cash)}
          sub={cf.emergency_fund_months === null ? 'no spending data yet' : `${cf.emergency_fund_months} months of spending`}
        />
        <Stat
          label="Savings rate"
          value={cf.savings_rate === null ? '—' : pct(cf.savings_rate)}
          sub={cf.income ? `${whole.format(cf.income)} in − ${whole.format(cf.spending)} out` : 'no income in the statements'}
        />
        <Stat
          label="Debt"
          value={whole.format(snapshot.totals.debts)}
          sub={mortgage?.apr ? `mortgage at ${mortgage.apr}%` : `${money.format(s.fees_and_interest)} interest & fees`}
        />
      </dl>

      <nav className="tabs" role="tablist" aria-label="Dashboard">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'tab active' : 'tab'} onClick={() => choose(t.id)}>
            {t.label}
          </button>
        ))}
      </nav>

      {/* Every tab stays mounted, so the question box keeps its conversation when you switch. */}
      <div role="tabpanel" hidden={tab !== 'overview'}>
      <section className="section">
        <div className="row">
          <h2>Review</h2>
          <button disabled={analysing} onClick={onRefresh}>{analysing ? 'Analysing…' : 'Re-analyse'}</button>
        </div>
        {error && <p className="error">✕ {error}</p>}
        {onRetrySave && !analysing && <button onClick={onRetrySave}>Sign in and save the review</button>}
        {reviewStale && !analysing && (
          <p className="notice"><span aria-hidden>!</span> Your corrections changed the figures below. The written review still uses the old ones until you press Re-analyse.</p>
        )}
        {analysing && <ReviewStatus progress={progress} />}
        {!saved && !analysing && <p className="muted">No review yet.</p>}
        {saved && (
          <>
            <p className="overview">{saved.overview}</p>
            <p className="label">Reviewed {saved.run_at} · figures from {snapshot.documents} statements, latest {snapshot.as_of ?? '—'}</p>
            {saved.findings.length > 0 && (
              <>
                <h3 className="subhead">Key findings</h3>
                <ul className="findings">
                  {saved.findings.map((f, i) => (
                    <li key={i} className={`card finding t-${f.tone}`}>
                      <span className="tone"><span aria-hidden>{TONE[f.tone].icon}</span> {TONE[f.tone].word}</span>
                      <h3>{f.title}</h3>
                      <p>{f.detail}</p>
                      <button className="link ask-link" onClick={() => onAsk(`About “${f.title}”: ${f.detail} `)}>Ask about this</button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </section>

      {children}

      <WhatChanged book={book} />

      {saved && (
        <section className="section">
          <h2>Recommendations</h2>
          <Recommendations rows={saved.recommendations} onStatus={onStatus} onAsk={onAsk} />
          <p className="label disclaimer">Generated by Claude from your statements. Not personalised investment, tax or legal advice; check anything consequential with a professional.</p>
        </section>
      )}

      {rangePicker}

      <div className="grid">
        <section className="section">
          <h2>Net worth</h2>
          <NetWorthChart points={netWorth} />
        </section>
        <section className="section">
          <h2>Balance sheet</h2>
          <Bars
            color="var(--series-1)"
            items={snapshot.groups.filter((g) => g.group !== 'debt')}
            total={snapshot.totals.assets}
            name={(g) => GROUP_LABEL[g as Group] ?? g}
            note={(g) => `${g.count} account${g.count === 1 ? '' : 's'}`}
          />
          {snapshot.totals.debts > 0 && (
            <>
              <p className="label gap">Owed</p>
              <Bars color="var(--series-2)" items={snapshot.groups.filter((g) => g.group === 'debt')} name={() => 'Debt'} />
            </>
          )}
        </section>
      </div>

      <section className="section">
        <h2>What net worth is made of</h2>
        <NetWorthBands points={netWorth} />
      </section>

      <section className="section">
        <h2>Emergency fund</h2>
        <EmergencyFund months={cf.emergency_fund_months} target={emergencyTarget(book.profile)} monthly={cf.avg_monthly_spending} />
      </section>
      </div>

      <div role="tabpanel" hidden={tab !== 'spending'}>
        <MerchantSearch ledger={book.ledger} onOpen={onDrill} />
        {rangePicker}
        <section className="section">
          <h2>Cash flow</h2>
          <CashFlowChart months={shownFlows} details={topOf} onSelect={(month) => drill({ title: `All activity in ${month}`, month, windowOnly: true })} />
          <p className="muted small">
            Checking, savings and card activity only; transfers between your own accounts, investment trades and one-offs are left out. Click a month
            for its transactions, and correct any that are counted wrongly.
          </p>
        </section>

        <section className="section">
          <h2>Savings rate</h2>
          <SavingsRateChart months={rates.months} overall={rates.overall} />
        </section>

        <section className="section">
          <h2>Where the income went</h2>
          <p className="label">{spanText}</p>
          <IncomeFlow income={income} categories={sp.by_category} />
        </section>

      <div className="grid">
        <section className="section">
          <div className="row">
            <h2>Spending by category</h2>
            <button className="quiet" onClick={() => onDrill({ title: 'All transactions', windowOnly: false })}>All transactions</button>
          </div>
          <p className="label">{sp.from ?? '—'} to {asOf ?? '—'} · {whole.format(sp.total)} · {whole.format(perMonth)} a month</p>
          <Bars color="var(--series-2)" items={sp.by_category} total={sp.total} onSelect={(g) => spendingIn(`Spending on ${g.toLowerCase()}`, { group: g })} />
        </section>
        <section className="section">
          <h2>Top merchants</h2>
          <Bars
            color="var(--series-2)"
            items={sp.top_merchants}
            total={sp.total}
            note={(m) => `${m.count} charge${m.count === 1 ? '' : 's'}`}
            onSelect={(m) => spendingIn(`Spending at ${m}`, { merchant: m })}
          />
        </section>
      </div>

      <section className="section">
        <h2>{compared ? `${compared} against the usual month` : 'This month against the usual'}</h2>
        <VersusUsualChart rows={usual} month={compared ?? ''} onSelect={(g) => spendingIn(`Spending on ${g.toLowerCase()}`, { group: g })} />
        <p className="muted small">The latest month that is nearly or fully over, against the other months of the range shown. Click a category for its transactions.</p>
      </section>

      <section className="section">
        <h2>Category trends</h2>
        <CategoryTrends series={categoryTrends(ledger, months)} months={months} onSelect={(g) => spendingIn(`Spending on ${g.toLowerCase()}`, { group: g })} />
      </section>

      <div className="grid">
        <section className="section">
          <h2>Largest outflows</h2>
          <div className="scroll">
            <table className="table">
              <thead><tr><th>Date</th><th>Merchant</th><th>Category</th><th className="r">Amount</th></tr></thead>
              <tbody>
                {sp.largest.map((t, i) => (
                  <tr key={i}>
                    <td className="num">{t.date}</td>
                    <td><button className="link" onClick={() => spendingIn(`Spending at ${t.merchant}`, { merchant: t.merchant })}>{t.merchant}</button></td>
                    <td className="muted">{book.categories.label(t.category)}</td>
                    <td className="r num">{money.format(t.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section className="section">
          <h2>Recurring charges</h2>
          {s.recurring.length === 0 ? (
            <p className="muted">None found yet. Recurring charges show up once two or more months of statements are in.</p>
          ) : (
            <table className="table">
              <thead><tr><th>Merchant</th><th className="r">Typical</th><th className="r">Months</th><th className="r">A year</th></tr></thead>
              <tbody>
                {s.recurring.map((r) => (
                  <tr key={r.merchant}>
                    <td><button className="link" onClick={() => spendingIn(`Spending at ${r.merchant}`, { merchant: r.merchant })}>{r.merchant}</button></td>
                    <td className="r num">{money.format(r.typical)}</td>
                    <td className="r num">{r.months}</td>
                    <td className="r num">{whole.format(r.typical * 12)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {s.recurring.length > 0 && (
        <section className="section">
          <h2>Recurring charges by month</h2>
          <RecurringChart months={recurringByMonth(ledger, s.recurring.map((r) => r.merchant), months)} />
        </section>
      )}

      <section className="section">
        <h2>Spending by day</h2>
        {span && earliest ? <SpendingCalendar days={dailySpending(ledger)} start={firstDay(span, earliest)!} end={span.to} /> : <p className="muted">No spending yet.</p>}
      </section>

      {sp.one_offs.count > 0 && (
        <section className="section">
          <div className="row">
            <h2>One-offs</h2>
            <button className="quiet" onClick={() => drill({ title: 'One-off purchases', treatments: ['one_off'], windowOnly: true })}>
              See all {sp.one_offs.count}
            </button>
          </div>
          <p className="muted small">
            {whole.format(sp.one_offs.total)} you marked as one-off. Real money out, but left out of spending, the monthly average and the savings rate.
          </p>
          <table className="table">
            <tbody>
              {sp.one_offs.items.map((t, i) => (
                <tr key={i}>
                  <td className="num">{t.date}</td>
                  <td>{t.merchant}</td>
                  <td className="muted">{book.categories.label(t.category)}</td>
                  <td className="r num">{money.format(t.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      </div>

      <div role="tabpanel" hidden={tab !== 'investments'}>
        <section className="section">
          <h2>Asset allocation</h2>
          <AllocationChart allocation={inv.allocation} total={inv.total} />
          <p className="muted small">
            {inv.weighted_expense_ratio !== null
              ? `Weighted expense ratio ${inv.weighted_expense_ratio.toFixed(3)}% across the ${pct(inv.expense_ratio_coverage)} of holdings whose ratio the statements print.`
              : 'The statements do not print expense ratios.'}
            {inv.unrealized_gain !== null && ` Unrealised gain where cost basis is shown: ${whole.format(inv.unrealized_gain)}.`}
          </p>
        </section>

      {inv.top_holdings.length > 0 && (
        <div className="grid">
          <section className="section">
            <h2>What the funds cost a year</h2>
            {fees.length === 0 ? (
              <p className="muted">The statements don't print expense ratios.</p>
            ) : (
              <>
                <p className="label">{whole.format(feeTotal)} a year across the largest holdings that print a ratio</p>
                <Bars
                  color="var(--series-2)"
                  items={fees.map((f) => ({ label: f.label, total: f.total, count: 0 }))}
                  name={(l) => `${l} · ${fees.find((f) => f.label === l)?.ratio}%`}
                  note={(a) => `${whole.format(fees.find((f) => f.label === a.label)?.value ?? 0)} held`}
                />
              </>
            )}
          </section>
          <section className="section">
            <h2>Gains and losses</h2>
            <p className="label">Unrealised, on the largest holdings with a cost basis · ▲ gain ▼ loss</p>
            <GainsChart holdings={inv.top_holdings} />
          </section>
        </div>
      )}

      {inv.top_holdings.length > 0 && (
        <section className="section">
          <h2>Largest holdings</h2>
          <div className="scroll">
            <table className="table">
              <thead>
                <tr><th>Holding</th><th>Class</th><th className="r">Value</th><th className="r">Share</th><th className="r">Expense ratio</th><th className="r">Unrealised</th></tr>
              </thead>
              <tbody>
                {inv.top_holdings.map((h) => (
                  <tr key={h.symbol || h.name}>
                    <td><span className="num">{h.symbol}</span> <span className="muted">{h.name}</span></td>
                    <td>{CLASS_LABEL[h.asset_class] ?? label(h.asset_class)}</td>
                    <td className="r num">{whole.format(h.value)}</td>
                    <td className="r num">{pct(h.share)}</td>
                    <td className="r num">{h.expense_ratio === null ? '—' : `${h.expense_ratio}%`}</td>
                    <td className="r num">{h.unrealized === null ? '—' : `${h.unrealized >= 0 ? '▲' : '▼'} ${whole.format(Math.abs(h.unrealized))}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      </div>

      <div role="tabpanel" hidden={tab !== 'accounts'}>
      <section className="section">
        <h2>Accounts</h2>
        <Accounts accounts={snapshot.accounts} onSelect={(a) => onDrill({ title: `${a.institution} ${a.name}`.trim(), account_id: a.account_id })} />
        <p className="muted small">
          Something without statements — your home, a car loan, a 401(k)? Add a row to the{' '}
          {sheetUrl ? <a href={sheetUrl} target="_blank" rel="noreferrer">Manual tab</a> : 'Manual tab'} of your spreadsheet:{' '}
          <span className="num">name</span>, <span className="num">type</span> (asset or debt), <span className="num">category</span> (home,
          vehicle, 401k, cash…), <span className="num">value</span>, <span className="num">as_of</span>, and for a debt its{' '}
          <span className="num">rate</span>. It counts in net worth and the review from the next refresh.
        </p>
      </section>

      <UsageLine rows={book.usage} sheetUrl={sheetUrl} />
      </div>
    </>
  )
}

const KIND_LABEL: Record<string, string> = { extract: 'reading documents', review: 'reviews', ask: 'questions' }

/** What this month's Claude calls cost, from the Usage tab. */
function UsageLine({ rows, sheetUrl }: { rows: Row[]; sheetUrl?: string }) {
  const month = new Date().toISOString().slice(0, 7)
  const u = summarise(rows, month)
  return (
    <section className="section">
      <h2>Claude API usage</h2>
      {u.calls === 0 ? (
        <p className="muted small">No calls recorded yet in {month}. Each document read, review and question is logged in the Usage tab with its tokens and cost.</p>
      ) : (
        <p className="muted small">
          {month}: <b className="num">{money.format(u.total)}</b> over {u.calls} call{u.calls === 1 ? '' : 's'} —{' '}
          {u.byKind.map((k) => `${KIND_LABEL[k.kind]} ${money.format(k.cost)} (${k.calls}, ${Math.round(k.seconds / k.calls)} s each)`).join(' · ')}.{' '}
          {sheetUrl ? <a href={sheetUrl} target="_blank" rel="noreferrer">Every call is in the Usage tab.</a> : 'Every call is in the Usage tab.'}
        </p>
      )}
    </section>
  )
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div>
      <dt className="label">{label}</dt>
      <dd className="num">{value}<div className="sub">{sub}</div></dd>
    </div>
  )
}

function Accounts({ accounts, onSelect }: { accounts: AccountSummary[]; onSelect: (a: AccountSummary) => void }) {
  const withBalance = accounts.filter((a) => a.balance !== null)
  return (
    <div className="scroll">
      <table className="table">
        <thead>
          <tr><th>Account</th><th className="r">Balance</th><th className="r">Rate</th><th className="r">Limit used</th><th>As of</th></tr>
        </thead>
        {GROUP_ORDER.map((g) => {
          const rows = withBalance.filter((a) => a.group === g).sort((a, b) => Math.abs(b.balance!) - Math.abs(a.balance!))
          if (!rows.length) return null
          const subtotal = rows.reduce((t, a) => t + a.balance!, 0)
          return (
            <tbody key={g}>
              <tr className="group">
                <td>{GROUP_LABEL[g]}</td>
                <td className="r num">{whole.format(subtotal)}</td>
                <td colSpan={3} />
              </tr>
              {rows.map((a) => (
                <tr key={a.account_id}>
                  <td>
                    {a.manual ? `${a.institution} ${a.name}` : <button className="link" onClick={() => onSelect(a)}>{a.institution} {a.name}</button>}
                    {a.last4 && <span className="muted"> ··{a.last4}</span>}
                    {a.manual && <span className="label"> · entered by hand</span>}
                  </td>
                  <td className="r num">{money.format(a.balance!)}</td>
                  <td className="r num">{a.apr === null ? '' : `${a.apr}%`}</td>
                  <td className="r num">{a.utilization === null ? '' : `${pct(a.utilization)} of ${whole.format(a.credit_limit!)}`}</td>
                  <td className="num">{a.as_of ?? ''}</td>
                </tr>
              ))}
            </tbody>
          )
        })}
      </table>
    </div>
  )
}

function Recommendations({ rows, onStatus, onAsk }: { rows: Row[]; onStatus: Props['onStatus']; onAsk: Props['onAsk'] }) {
  const open = rows
    .filter((r) => r.status === 'new')
    .sort((a, b) => (PRIORITY_RANK[String(a.priority)] ?? 3) - (PRIORITY_RANK[String(b.priority)] ?? 3))
  const closed = rows.filter((r) => r.status !== 'new')
  return (
    <>
      <ol className="recs">
        {open.map((r) => (
          <li key={String(r.rec_id)} className="rec card">
            <div className="rec-head">
              <span className="label">
                {r.priority && <span className={`priority p-${r.priority}`}>{label(String(r.priority))}</span>} {AREA[String(r.area)] ?? String(r.area)}
              </span>
              {typeof r.est_annual_value === 'number' && <span className="worth num">≈ {whole.format(r.est_annual_value)}/yr</span>}
            </div>
            <h3>{r.title}</h3>
            {r.next_step && <p className="next"><b>Next step:</b> {r.next_step}</p>}
            <details className="why">
              <summary>Why</summary>
              <p>{r.detail}</p>
            </details>
            <div className="rec-actions">
              <button onClick={() => onStatus(String(r.rec_id), 'done')}>✓ Done</button>
              <button className="quiet" onClick={() => onStatus(String(r.rec_id), 'dismissed')}>✕ Dismiss</button>
              <button className="quiet" onClick={() => onAsk(`About the recommendation “${r.title}” (next step: ${r.next_step}): `)}>Ask about this</button>
            </div>
          </li>
        ))}
      </ol>
      {closed.length > 0 && (
        <details className="closed">
          <summary>{closed.length} done or dismissed</summary>
          <ul>
            {closed.map((r) => (
              <li key={String(r.rec_id)}>
                <span className="label">{r.status === 'done' ? '✓ Done' : '✕ Dismissed'}</span> {r.title}{' '}
                <button className="link" onClick={() => onStatus(String(r.rec_id), 'new')}>Reopen</button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  )
}

const STAGES: { stage: ReviewProgress['stage']; word: string }[] = [
  { stage: 'reading', word: 'Read figures' },
  { stage: 'thinking', word: 'Think' },
  { stage: 'writing', word: 'Write' },
  { stage: 'saving', word: 'Save' },
]

/**
 * A running review: the four stages (done ✓, current ●, still to come ○ and
 * lighter, so nothing rests on colour), what is happening now, Claude's
 * latest thinking heading, and the time so far.
 */
function ReviewStatus({ progress }: { progress?: ReviewProgress }) {
  const [start] = useState(() => Date.now())
  const [now, setNow] = useState(start)
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const seconds = Math.floor((now - start) / 1000)
  const at = STAGES.findIndex((s) => s.stage === (progress?.stage ?? 'reading'))
  return (
    <div className="review-status" role="status" aria-live="polite">
      <ol className="stages">
        {STAGES.map((s, i) => (
          <li key={s.stage} className={i < at ? 'done' : i === at ? 'current' : 'todo'}>
            <span aria-hidden>{i < at ? '✓' : i === at ? '●' : '○'}</span> {s.word}
          </li>
        ))}
        <li className="elapsed num">{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</li>
      </ol>
      <p>{progress?.note ?? 'Starting'}</p>
      {progress?.stage === 'thinking' && progress.thought && <p className="muted thought">{progress.thought}</p>}
      <p className="muted small">A review usually takes three to seven minutes; checking current card and account terms on the web adds time.</p>
    </div>
  )
}
